import eventBus from "@/domain/core/constant/eventBus"

const state = {
    regions: null,
    contracts: [],
    categories: null,
    basePlans: null,
    region: null,
    columnFilterValue: {},
    activePage: 1,
    contract: null,
    hasOtherContracts: false,
    canCreateNew: false,
}

export const getters = {
    regions: state => state.regions,
    region: state => state.region,
    contracts: state => state.contracts,
    categories: state => state.categories,
    basePlans: state => state.basePlans,
    contract: state => state.contract,
    hasOtherContracts: state => state.hasOtherContracts,
    canCreateNew: state => state.canCreateNew,
    urlSessionParams: () => {
        const memory = sessionStorage.getItem('event_url')
        if (memory) return JSON.parse(memory)
        return {}
    },
    columnFilterValue: state => state.columnFilterValue,
    activePage: state => state.activePage,

}


export const mutations = {
    resetState(state) {
        state.regions = null
        state.contracts = []
        state.categories = null
        state.basePlans = null
        state.region = null
        state.columnFilterValue = {}
        state.activePage = 1
        state.contract = null
        state.hasOtherContracts = false
        state.canCreateNew = false
    },
    regions(state, regions) {
        state.regions = regions
    },
    region(state, region) {
        state.region = region
        state.base_plans = null
    },
    contracts(state, contracts) {
        state.contracts = contracts
        // Only reset contract if it's not in the new list
        if (state.contract && contracts && !contracts.find(c => c.id === state.contract.id)) {
            state.contract = null
        }
    },
    hasOtherContracts(state, hasOther) {
        state.hasOtherContracts = hasOther
    },
    canCreateNew(state, canCreate) {
        state.canCreateNew = canCreate
    },
    categories(state, categories) {
        state.categories = categories
    },
    basePlans(state, basePlans) {
        state.basePlans = basePlans
    },
    contract(state, contract) {
        state.contract = contract
    },
    rememberUrl(state, {region, plan}) {
        if (!region && !plan) {
            sessionStorage.removeItem('event_url')
            return
        }

        const memory = {
            region: region,
            plan: plan
        }
        sessionStorage.setItem('event_url', JSON.stringify(memory))
    },
    setColumnFilterValue(state, value) {
        state.columnFilterValue = value
    },
    setActivePage(state, value) {
        state.activePage = value
    }
}


export const actions = service => ({
    // eslint-disable-next-line no-unused-vars
    async createNewEvent({commit}, data) {
        const newEvent = await service.createNewEvent(data)
        commit('rememberUrl', {})
        return newEvent
    },
    async fetchCreateConfig({commit, getters}, contractId = null) {
        const config = await service.fetchCreateConfig(getters['urlSessionParams'], contractId)
        if (config['region']) {
            commit('regions', null)
            commit('region', config['region'])
            commit('categories', config['categories'])
            commit('basePlans', config['base_plans'])
        } else {
            commit('region', null)
            commit('regions', config['regions'])
            commit('categories', null)
            commit('basePlans', null)
        }
    },
    async setRegion({commit}, region) {
        commit('region', region)
    },
    async fetchCategories({commit, getters}) {
        commit('categories', await service.fetchCategories(getters['region'].id))
    },
    rememberUrl({commit}, {region, plan}) {
        commit('rememberUrl', {region, plan})
    },
    updateColumnFilterValue({commit}, value) {
        commit('setColumnFilterValue', value)
    },
    updateActivePage({commit}, value) {
        commit('setActivePage', value)
    },
    async fetchContracts({commit, dispatch, state, rootState}) {
        const response = await service.fetchContracts()
        
        commit('contracts', response.contracts)
        commit('hasOtherContracts', response.other)
        commit('canCreateNew', response.can_create_new)
        
        // If we have a contract and it's still in the list (or we have 'other' contracts), keep it
        if (state.contract && state.contract.id) {
            const contractStillValid = response.contracts.find(c => c.id === state.contract.id) || response.other
            if (contractStillValid) {
                return // Contract is still valid, don't change it
            }
        }
        
        // Only set active contract if we don't have one already or current one is invalid
        if (!state.contract || !state.contract.id) {
            if (response.other) {
                await dispatch('setActiveContract', -1)
            } else if (response.contracts && response.contracts.length > 0) {
                await dispatch('setActiveContract', response.contracts[0].id)
            }
        }
    },
    async setActiveContract({commit, dispatch, state}, contractId) {
        // Convert to number if string
        const numContractId = parseInt(contractId)
        
        if (numContractId && numContractId > 0) {
            const contract = await service.fetchContract(numContractId)
            commit('contract', contract)
            
            // Fetch permissions with valid contract ID
            await dispatch('login/fetchConfig', {
                type: 'contract',
                id: numContractId
            }, {root: true})
        } else {
            commit('contract', null)
            
            // Fetch permissions with -1 for "other" contracts
            await dispatch('login/fetchConfig', {
                type: 'contract',
                id: -1
            }, {root: true})
        }

        // Notify of contract change
        await dispatch('eventBus/emitEvent', {
            event: eventBus.contract.changed,
            payload: getters['contract']
        }, {root: true})
    }
})

export default eventService => ({
    namespaced: true,
    state,
    getters,
    mutations,
    actions: actions(eventService)
})
